import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Clock, DollarSign, ArrowRight, X, MapPin, Banknote, Languages, Globe, ChevronRight, FileText } from "lucide-react";
import { Input } from "../components/ui/input";
import { useNavigate } from "react-router-dom";
import { useCurrency } from "../context/CurrencyContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VisaSearch() {
  const [selectedCountry, setSelectedCountry] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [detailCard, setDetailCard] = useState(null);
  const [countries, setCountries] = useState([]);
  const [regionFilter, setRegionFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const { convert } = useCurrency();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/countries`)
      .then(r => { setCountries(r.data); setError(false); })
      .catch(() => { setError(true); })
      .finally(() => setLoading(false));
  }, []);

  const filtered = countries.filter(
    (c) => (!selectedCountry || selectedCountry === "all" || c.country_code === selectedCountry) &&
      (!searchQuery || c.country_name.toLowerCase().includes(searchQuery.toLowerCase())) &&
      (regionFilter === "all" || c.continent === regionFilter)
  );
  const displayed = showAll ? filtered : filtered.slice(0, 20);

  return (
    <section id="visa-search" data-testid="visa-search-section" className="py-20 md:py-28 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <span className="text-xs tracking-widest uppercase font-semibold text-blue-600 mb-3 block">Popular Destinations</span>
          <h2 className="heading-font text-3xl md:text-4xl font-bold text-slate-900">Browse Countries</h2>
          <p className="text-slate-500 mt-3 max-w-lg mx-auto">Choose from 120+ destinations. See pricing, requirements, and processing times upfront.</p>
        </motion.div>

        {/* Region tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          {["all", "Asia", "Europe", "North America", "Middle East", "Africa", "South America", "Oceania"].map(r => (
            <button key={r} onClick={() => { setRegionFilter(r); setShowAll(false); }}
              className={`text-xs font-medium px-4 py-1.5 rounded-full transition-all ${regionFilter === r ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
              {r === "all" ? `All (${countries.length})` : r}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="card-elevated p-4 md:p-5 max-w-3xl mx-auto mb-12">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input data-testid="visa-search-input" placeholder="Search country..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                className="pl-10 h-11 border-slate-200 rounded-lg focus-visible:ring-blue-500/30 focus-visible:border-blue-500" />
            </div>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="text-center py-16"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" /><p className="text-sm text-slate-400">Loading countries...</p></div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="text-center py-16 card-elevated rounded-2xl mx-auto max-w-md p-8">
            <p className="text-slate-600 font-medium mb-2">Unable to load countries</p>
            <p className="text-sm text-slate-400 mb-4">Please check your internet connection and try again.</p>
            <button onClick={() => { setLoading(true); axios.get(`${API}/countries`).then(r => { setCountries(r.data); setError(false); }).catch(() => setError(true)).finally(() => setLoading(false)); }}
              className="text-sm text-white bg-blue-600 px-5 py-2.5 rounded-lg hover:bg-blue-700">Retry</button>
          </div>
        )}

        {/* Country Cards Grid */}
        {!loading && !error && countries.length > 0 && (
          <>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            {displayed.map((card, i) => (
              <motion.div key={card.country_code} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: Math.min(i * 0.03, 0.3), duration: 0.4 }}
                className="card-elevated overflow-hidden group cursor-pointer" data-testid={`visa-card-${card.country_code?.toLowerCase()}`}
                onClick={() => setDetailCard(card)}>
                <div className="h-28 md:h-36 relative overflow-hidden">
                  <img src={card.image} alt={card.country_name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-transparent to-transparent" />
                  <div className="absolute bottom-2 left-2 md:left-3 flex items-center gap-1.5">
                    <span className="text-base md:text-xl">{card.flag}</span>
                    <span className="text-xs md:text-sm font-bold text-slate-900 heading-font">{card.country_name}</span>
                  </div>
                </div>
                <div className="p-3 md:p-4">
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <span className="text-[10px] md:text-[11px] font-semibold text-blue-600 bg-blue-50 px-1.5 md:px-2 py-0.5 rounded-full">{card.visa_types?.[0] || "Tourist"}</span>
                    <span className="text-[9px] md:text-[11px] text-slate-400 hidden sm:block">{card.validity || card.entry_type}</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-[10px] md:text-xs text-slate-500"><Clock className="w-3 h-3" />5-7 Days</div>
                    <div className="flex items-center gap-1.5 text-[10px] md:text-xs text-slate-500"><span className="text-blue-500 font-semibold">{convert(card.base_price)}</span></div>
                  </div>
                  <button className="w-full mt-2 md:mt-3 py-1.5 md:py-2 text-[10px] md:text-xs font-semibold text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all flex items-center justify-center gap-1">
                    View Details <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
          {filtered.length > 20 && !showAll && (
            <div className="text-center mt-10">
              <button onClick={() => setShowAll(true)} data-testid="show-more-countries"
                className="text-sm text-blue-600 font-semibold px-8 py-3 rounded-lg border border-blue-200 hover:bg-blue-50 transition-all">
                Show All {filtered.length} Destinations
              </button>
            </div>
          )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {detailCard && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={() => setDetailCard(null)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-100" onClick={e => e.stopPropagation()}
              data-testid="country-detail-modal">
              <div className="relative h-48 overflow-hidden rounded-t-2xl">
                <img src={detailCard.image} alt={detailCard.country_name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/30 to-transparent" />
                <button onClick={() => setDetailCard(null)} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/80 backdrop-blur flex items-center justify-center hover:bg-white" data-testid="close-detail-modal">
                  <X className="w-4 h-4 text-slate-600" />
                </button>
                <div className="absolute bottom-4 left-5 flex items-center gap-3">
                  <span className="text-3xl">{detailCard.flag}</span>
                  <div>
                    <h3 className="heading-font text-2xl font-bold text-slate-900">{detailCard.country_name}</h3>
                    <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{detailCard.visa_types?.[0] || "Tourist"}</span>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-5">
                <p className="text-slate-500 text-sm leading-relaxed">{detailCard.description}</p>
                <div className="grid grid-cols-2 gap-3">
                  {[{ icon: MapPin, l: "Capital", v: detailCard.capital }, { icon: Banknote, l: "Currency", v: detailCard.currency },
                    { icon: Languages, l: "Language", v: detailCard.language }, { icon: Globe, l: "Continent", v: detailCard.continent }].map(i => (
                    <div key={i.l} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                      <div className="flex items-center gap-1.5 mb-0.5"><i.icon className="w-3.5 h-3.5 text-blue-500" /><span className="text-[11px] text-slate-400">{i.l}</span></div>
                      <span className="text-sm text-slate-700 font-medium">{i.v}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-800 mb-2">Visa Details</h4>
                  <div className="space-y-1.5">
                    {[["Validity", detailCard.validity || "Varies"], ["Entry Type", detailCard.entry_type || "Single/Multiple"], ["Fee", convert(detailCard.base_price)]].map(([l, v]) => (
                      <div key={l} className="flex justify-between text-sm"><span className="text-slate-400">{l}</span><span className={l === "Fee" ? "text-blue-600 font-semibold" : "text-slate-700"}>{v}</span></div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-800 mb-2">Required Documents</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {(detailCard.required_docs || []).map(d => <span key={d} className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-100">{d}</span>)}
                  </div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button data-testid="apply-from-detail" onClick={() => { setDetailCard(null); navigate(`/pricing?destination=${detailCard.country_code}`); }}
                    className="flex-1 py-3 text-white font-semibold bg-blue-600 rounded-xl hover:bg-blue-700 transition-all shadow-sm">Check Pricing & Apply</button>
                  <button onClick={() => { setDetailCard(null); navigate(`/visa/${detailCard.country_code}`); }}
                    className="py-3 px-5 text-slate-600 font-medium rounded-xl border border-slate-200 hover:bg-slate-50 transition-all">Full Page</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
